import React, { useEffect, useId, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export interface HeroStrip {
  id: string;
  label: string;
  blurb: string;
  to: string;
  image: string;
  /** Keep the photo black and white when the strip opens, too. */
  mono?: boolean;
}

interface HeroAccordionProps {
  strips: HeroStrip[];
  onActiveChange?: (active: boolean) => void;
  className?: string;
}

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * Two jagged left edges, drawn in objectBoundingBox units so they scale with the strip.
 * Alternating them keeps neighbouring tears from looking cloned.
 */
const TEAR_PATHS = [
  'M0.055 0 L0.02 0.06 L0.06 0.11 L0.03 0.19 L0.065 0.25 L0.025 0.33 L0.055 0.4 L0.02 0.47 L0.06 0.55 L0.03 0.62 L0.065 0.7 L0.025 0.78 L0.055 0.85 L0.02 0.92 L0.05 1 L1 1 L1 0 Z',
  'M0.03 0 L0.065 0.05 L0.025 0.13 L0.06 0.2 L0.02 0.28 L0.055 0.36 L0.025 0.43 L0.065 0.5 L0.03 0.58 L0.06 0.66 L0.02 0.73 L0.055 0.81 L0.025 0.88 L0.06 0.95 L0.035 1 L1 1 L1 0 Z',
];

/** Same tears along the top edge, for the stacked mobile layout (x and y swapped). */
const TEAR_PATHS_TOP = TEAR_PATHS.map((d) =>
  d.replace(/([ML])([\d.]+) ([\d.]+)/g, (_m, cmd, x, y) => `${cmd}${y} ${x}`),
);

const DESKTOP = '(min-width: 1024px)';

function useIsDesktop(): boolean {
  const [desktop, setDesktop] = useState(() => (typeof window === 'undefined' ? true : window.matchMedia(DESKTOP).matches));
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP);
    const onChange = () => setDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return desktop;
}

const HeroAccordion: React.FC<HeroAccordionProps> = ({ strips, onActiveChange, className = '' }) => {
  const [active, setActive] = useState<number | null>(null);
  const clipBase = useId().replace(/:/g, '');
  const desktop = useIsDesktop();

  const activate = (i: number) => {
    setActive(i);
    onActiveChange?.(true);
  };
  const reset = () => {
    setActive(null);
    onActiveChange?.(false);
  };

  return (
    <div className={`relative h-full ${className}`}>
      <svg width="0" height="0" className="absolute" aria-hidden="true">
        <defs>
          {TEAR_PATHS.map((d, i) => (
            <clipPath key={i} id={`${clipBase}-tear-${i}`} clipPathUnits="objectBoundingBox">
              <path d={d} />
            </clipPath>
          ))}
          {TEAR_PATHS_TOP.map((d, i) => (
            <clipPath key={`t${i}`} id={`${clipBase}-teartop-${i}`} clipPathUnits="objectBoundingBox">
              <path d={d} />
            </clipPath>
          ))}
        </defs>
      </svg>

      <div
        className="flex flex-col lg:flex-row h-full w-full"
        onMouseLeave={reset}
        role="list"
        aria-label="Our services"
      >
        {strips.map((strip, i) => {
          const isActive = active === i;
          const anyActive = active !== null;
          const tear = desktop ? `url(#${clipBase}-tear-${i % 2})` : `url(#${clipBase}-teartop-${i % 2})`;
          return (
            <div
              key={strip.id}
              role="listitem"
              className="relative min-w-0 min-h-0 motion-reduce:transition-none"
              style={{
                flexGrow: isActive ? (desktop ? 3.4 : 3.8) : 1,
                flexBasis: 0,
                marginLeft: desktop && i > 0 ? '-18px' : 0,
                marginTop: !desktop && i > 0 ? '-18px' : 0,
                zIndex: i + 1,
                transition: `flex-grow 620ms ${EASE}`,
                filter: i === 0 ? 'none' : desktop ? 'drop-shadow(-8px 0 14px rgba(0,0,0,0.5))' : 'drop-shadow(0 -8px 14px rgba(0,0,0,0.5))',
              }}
            >
              <Link
                to={strip.to}
                // Hover only for a real mouse: touch fires a synthetic mouseenter before click,
                // which used to expand and navigate in the same tap.
                onPointerEnter={(e) => { if (e.pointerType === 'mouse') activate(i); }}
                onFocus={(e) => { if (e.currentTarget.matches(':focus-visible')) activate(i); }}
                onClick={(e) => {
                  // First tap expands; second tap follows the link.
                  if (!isActive) {
                    e.preventDefault();
                    activate(i);
                  }
                }}
                aria-expanded={isActive}
                aria-label={`${strip.label}: ${strip.blurb}`}
                className="group relative block h-full w-full overflow-hidden text-left text-white outline-none focus-visible:ring-2 focus-visible:ring-[#78BE20] focus-visible:ring-inset"
                style={{ clipPath: i === 0 ? undefined : tear }}
              >
                {/* Photo */}
                <div
                  className="absolute inset-0 bg-cover bg-center will-change-transform motion-reduce:transition-none"
                  style={{
                    backgroundImage: `url(${strip.image})`,
                    transform: `scale(${isActive ? 1 : 1.18})`,
                    filter: isActive ? `grayscale(${strip.mono ? 1 : 0}) brightness(1)` : 'grayscale(1) brightness(0.75)',
                    transition: `transform 900ms ${EASE}, filter 600ms ${EASE}`,
                  }}
                />
                {/* Shade */}
                <div
                  className="absolute inset-0 motion-reduce:transition-none"
                  style={{
                    background: isActive
                      ? 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0.05) 100%)'
                      : desktop
                        ? 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 100%)'
                        : 'linear-gradient(to right, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 100%)',
                    opacity: anyActive && !isActive ? 1 : isActive ? 1 : 0.9,
                    transition: `opacity 500ms ${EASE}`,
                  }}
                />

                {/* Collapsed label: vertical on desktop (reads bottom to top), a horizontal band on mobile */}
                <span
                  className="absolute flex items-center gap-3 lg:gap-4 font-montserrat font-extrabold uppercase tracking-[0.08em] text-[18px] sm:text-[20px] lg:text-[30px] whitespace-nowrap left-6 top-1/2 lg:left-1/2 lg:top-auto lg:bottom-8 motion-reduce:transition-none"
                  style={{
                    writingMode: desktop ? 'vertical-rl' : 'horizontal-tb',
                    transform: desktop
                      ? `translateX(-50%) rotate(180deg) translateY(${isActive ? '-12px' : '0'})`
                      : `translateY(-50%) translateX(${isActive ? '12px' : '0'})`,
                    opacity: isActive ? 0 : 1,
                    transition: `opacity 220ms ${EASE}, transform 420ms ${EASE}`,
                    paddingLeft: desktop && i > 0 ? '10px' : 0,
                    paddingTop: !desktop && i > 0 ? '10px' : 0,
                    textShadow: '0 2px 12px rgba(0,0,0,0.7)',
                  }}
                >
                  {desktop ? null : <span className="block h-[3px] w-8 bg-[#78BE20]" aria-hidden="true" />}
                  {strip.label}
                  {desktop ? <span className="block w-[3px] h-8 bg-[#78BE20]" aria-hidden="true" /> : null}
                </span>

                {/* Expanded content */}
                <div
                  className="absolute inset-x-0 bottom-0 p-5 pl-6 lg:p-8 lg:pl-12 motion-reduce:transition-none"
                  style={{
                    opacity: isActive ? 1 : 0,
                    transform: `translateY(${isActive ? '0' : '14px'})`,
                    transition: isActive
                      ? `opacity 380ms ${EASE} 220ms, transform 520ms ${EASE} 200ms`
                      : `opacity 160ms ${EASE}, transform 200ms ${EASE}`,
                    pointerEvents: isActive ? 'auto' : 'none',
                  }}
                >
                  <h3 className="font-montserrat font-extrabold tracking-[-0.02em] leading-none text-[24px] md:text-[34px] whitespace-nowrap">
                    {strip.label}
                  </h3>
                  <p className="font-poppins text-[13px] md:text-sm leading-5 md:leading-6 text-white/80 mt-2 md:mt-3 max-w-[300px] lg:max-w-[260px]">
                    {strip.blurb}
                  </p>
                  <span className="mt-3 md:mt-5 inline-flex items-center gap-2 font-poppins font-semibold text-[11px] tracking-[0.14em] uppercase text-[#78BE20]">
                    Explore
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HeroAccordion;
